import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import React, { useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import { GradientText, ImpactMetric } from '../../components/ui/index.tsx';
import type { AgedCareCosts } from '../../../../shared/src/calculation/agedCareCostTypes.ts';

/** One row of a CalculationTooltip breakdown (see components/ui/CalculationTooltip.tsx). */
export interface BreakdownRow {
    label: string;
    value: number | string;
    emphasis?: boolean;
}

/** Before/after financial snapshot as built by AgedCareAnalyser (subset of calc-engine `financials`). */
export interface FinancialPosition {
    netWorth: number;
    cashFlow: number;
    cashFlowBreakdown?: BreakdownRow[];
    socialSecurity?: number;
    socialSecurityBreakdown?: BreakdownRow[];
    [key: string]: unknown;
}

export interface ProjectionYear {
    netWorth?: number;
    [key: string]: unknown;
}

export interface FinancialImpactPanelProps {
    before: FinancialPosition;
    /**
     * The panel IS the benchmark: the Suggestion Engine's plan summary holds the walk-in position
     * while every card carries its own plan, so each row shows one figure and no change. Manual
     * Control never sets this — there the rail is the plan, measured against the baseline.
     */
    baselineOnly?: boolean;
    after: FinancialPosition;
    agedCareCosts?: AgedCareCosts | null;
    legacyYears: number | string;
    setLegacyYears: (years: number) => void;
    /** Accepted for callers that already track it; not currently read by this panel. */
    radPaid?: number;
    onViewProjections: () => void;
    projections?: ProjectionYear[];
    cashFlowAfterValue?: number | null;
    /**
     * The BASELINE cashflow, already net of whatever care costs the baseline itself incurs.
     *
     * The panel subtracts the plan's care costs from the "after" but cannot know what the "before"
     * costs — so when the baseline stopped being "today, at home" (no care fees) and became "in care,
     * no plan" (full DAP), the tile compared a figure net of fees with one gross of them. Caller
     * supplies it, because only the caller knows what its own baseline costs.
     */
    cashFlowBeforeValue?: number | null;
    cashFlowBreakdownOverride?: BreakdownRow[] | null;
    pensionBreakdownOverride?: BreakdownRow[] | null;
    netWorthAfterValue?: number | null;
    netWorthBreakdownOverride?: BreakdownRow[] | null;
    legacyEstimateOverride?: number | null;
    /**
     * The estate the client would reach by DOING NOTHING, over the same period. When supplied it
     * becomes the estate tile's "before", which is the only benchmark that answers "does this
     * strategy cost my estate anything". Omitted, the tile falls back to today's net worth.
     */
    legacyBaselineValue?: number | null;
    /**
     * What the "before" column IS, in the client's words — "no plan", "staying put". Named on the
     * panel and on every tile, because a comparison the reader cannot identify is worse than none:
     * the same figure means opposite things depending on the benchmark behind it.
     */
    baselineLabel?: string;
    legacyBreakdownOverride?: BreakdownRow[] | null;
    /**
     * Render as a SECTION of a larger panel rather than as a card of its own.
     *
     * Opt-in, and only the Aged Care module passes it: that module now gathers the costs, these
     * tiles and the narrative into one PlanSummaryPanel, which supplies the heading, names the
     * baseline and owns the way through to the projections. Left standalone here, all three would
     * appear twice — and duplication in the rail is exactly what the consolidation removed.
     *
     * What it suppresses is only chrome. Every tile, figure and breakdown is identical either way.
     * The Project-Legacy-Over control stays: it is an input, not decoration, and losing it would
     * take the projection period away from the reader (owner-caught in the mock-up, 2026-08-26).
     */
    embedded?: boolean;
    /**
     * The estate tile's title. Granny flat passes "What's left in their estate": what goes to the
     * person they live with has left the estate but is not lost (owner, 19 Oct 2026).
     */
    legacyTitle?: string;
    /**
     * A figure here is still being worked out (utils/calcTracker.ts): the tiles say so instead of showing a half-new
     * comparison — never "vs today" while the real benchmark is on its way (25 Oct 2026). The period input stays.
     */
    working?: boolean;
}

const FinancialImpactPanel = ({
    before,
    after,
    agedCareCosts,
    legacyYears,
    setLegacyYears,
    onViewProjections,
    projections,
    cashFlowAfterValue,
    cashFlowBeforeValue,
    cashFlowBreakdownOverride,
    pensionBreakdownOverride,
    netWorthAfterValue,
    netWorthBreakdownOverride,
    legacyEstimateOverride,
    legacyBaselineValue = null,
    baselineLabel,
    legacyBreakdownOverride,
    embedded = false,
    baselineOnly = false,
    legacyTitle = "What's left to the family",
    working = false,
}: FinancialImpactPanelProps) => {
    /** Whether the "Annual Position vs View Projections" explanation is open. Collapsed by default. */
    const [basisExplainerOpen, setBasisExplainerOpen] = useState(false);
    const annualAgedCareCost = agedCareCosts?.totalDailyCost ? agedCareCosts.totalDailyCost * 365 : 0;
    const afterCashFlowWithCareCosts =
        cashFlowAfterValue != null ? cashFlowAfterValue : after.cashFlow - annualAgedCareCost;

    const cashFlowBreakdownWithCare = useMemo(() => {
        if (cashFlowBreakdownOverride) return cashFlowBreakdownOverride;
        if (!after.cashFlowBreakdown) return [];
        if (!annualAgedCareCost) return after.cashFlowBreakdown;
        return [
            ...after.cashFlowBreakdown,
            { label: '--- Aged Care Costs ---', value: '' },
            { label: 'Less: Annual Care Fees', value: -annualAgedCareCost },
        ];
    }, [after.cashFlowBreakdown, annualAgedCareCost, cashFlowBreakdownOverride]);

    const legacyEstimate = useMemo(() => {
        if (legacyEstimateOverride != null) return legacyEstimateOverride;
        if (!projections || projections.length === 0) return after.netWorth;
        const lastYear = projections[projections.length - 1];
        // Net of the tax on selling the investment properties (support/estateCgt.ts, 21 Oct 2026).
        return estateOf(lastYear) ?? after.netWorth;
    }, [projections, after.netWorth, legacyEstimateOverride]);

    const pensionAfter = after.socialSecurity ?? 0;
    const pensionBreakdown = pensionBreakdownOverride ?? after.socialSecurityBreakdown;
    const netWorthAfter = netWorthAfterValue != null ? netWorthAfterValue : after.netWorth;

    return (
        <div className={embedded ? '' : 'mt-6 pt-6 border-t border-gray-200'}>
            {embedded ? (
                // Embedded: the panel above already carries the heading and names the baseline, so
                // only the projection-period input survives — on its own line, because at this
                // pane's width (~391px at 100% zoom) a heading and a control cannot share a row.
                <div className="flex items-center gap-2 mb-3">
                    <label htmlFor="legacyYears" className="text-[13px] font-medium text-slate-600">Project legacy over</label>
                    <input
                        type="number"
                        id="legacyYears"
                        value={legacyYears}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLegacyYears(parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-[13px]"
                    />
                    <span className="text-[13px] text-slate-600">years</span>
                </div>
            ) : (
                <div className="flex justify-between items-center mb-4">
                    <h4 className="text-2xl font-bold">
                        <GradientText>Financial Impact</GradientText>
                    </h4>
                    <div className="flex items-center gap-2">
                        <label htmlFor="legacyYears" className="text-sm font-medium">Project Legacy Over</label>
                        <input
                            type="number"
                            id="legacyYears"
                            value={legacyYears}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLegacyYears(parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded-lg"
                        />
                        <span className="text-sm">years</span>
                    </div>
                </div>
            )}
            {/* NAME THE BENCHMARK. Every figure below is a difference, and a difference is
                meaningless until the reader knows what it is a difference FROM. Stated once, at the
                top, so it governs all four tiles rather than being inferred from each. */}
            {baselineLabel && !embedded && (
                <p className="mb-3 rounded-lg border border-slate-200/70 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700">
                    Every figure below is measured against{' '}
                    <span className="font-semibold text-slate-900">{baselineLabel}</span> — what this
                    household&apos;s position would be if no strategy were put in place. The grey figure in each
                    tile is that baseline; the bold figure is this plan.
                </p>
            )}
            {working ? (
                <p className="text-[13px] text-slate-500" role="status" aria-live="polite" data-testid="financial-impact-working">Working it out…</p>
            ) : (
            <div className="space-y-4">
                <div>
                    <div className="flex items-baseline justify-between mb-2">
                        <h5 className={embedded
                            ? 'text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400'
                            : 'text-sm font-semibold text-gray-500'}>Annual Position</h5>
                        {/* The owner's design, replacing an always-visible caption that tried to explain
                            in-line and managed only to confuse a reader who IS a financial adviser. A
                            link NAMED FOR THE EXACT QUESTION beats a sentence: zero clutter for the
                            reader who doesn't care, the full answer on demand for the one who does —
                            and it no longer duplicates the View Projections button sitting just below. */}
                        <button
                            type="button"
                            onClick={() => setBasisExplainerOpen((open) => !open)}
                            aria-expanded={basisExplainerOpen}
                            className="text-[13px] text-blue-600 hover:text-blue-800 underline"
                        >
                            Annual Position vs View Projections
                        </button>
                    </div>
                    {basisExplainerOpen && (
                        <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5 text-left">
                            <p className="text-[13px] text-slate-600 leading-relaxed">
                                The figures below are a <strong>normalised year</strong> — not a financial or calendar year. They show what your
                                Age Pension and cash flow would look like over 12 months if your whole plan were in place today, so you can see
                                the strategy&apos;s impact without the timing of events getting in the way.
                            </p>
                            <p className="text-[13px] text-slate-600 leading-relaxed mt-1.5">
                                <strong>View Projections</strong> shows the timing: year-by-year estimates on a financial or calendar-year basis,
                                including the year your plan starts — which blends part of the old position and part of the new.
                            </p>
                        </div>
                    )}
                    {/*
                      * Embedded: compact ROWS, not tiles. Four hero tiles stacked in the plan
                      * summary's ~391px pane is a lot of scrolling for four numbers, and the pane
                      * cannot go two-across below 2xl anyway (`md:` measures the viewport, not this
                      * pane — two tiles there would be ~176px each). Rows put the label on the left
                      * and before -> after -> delta on the right, so all four fit without scrolling.
                      * Standalone callers keep the tiles unchanged.
                      */}
                    <div className={embedded ? '' : 'grid grid-cols-1 2xl:grid-cols-2 gap-4 text-center'}>
                        <ImpactMetric
                            layout={embedded ? 'row' : 'card'}
                            title="Age Pension (p.a.)"
                            subtitle="estimated entitlement, over a year"
                            beforeValue={baselineOnly ? null : (before.socialSecurity ?? 0)}
                            afterValue={pensionAfter}
                            breakdown={pensionBreakdown}
                        />
                        <ImpactMetric
                            layout={embedded ? 'row' : 'card'}
                            title="Cash Flow (p.a.)"
                            subtitle="money in, less money out, over a year"
                            beforeValue={baselineOnly ? null : (cashFlowBeforeValue != null ? cashFlowBeforeValue : before.cashFlow)}
                            afterValue={afterCashFlowWithCareCosts}
                            breakdown={cashFlowBreakdownWithCare}
                        />
                    </div>
                </div>
                <div>
                    <h5 className={`mb-2 ${embedded
                        ? 'text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400'
                        : 'text-sm font-semibold text-gray-500'}`}>Wealth Position</h5>
                    {/* `2xl:`, not `md:`. This panel lives in the analyser's right pane, which is
                        ~384–650px between xl and 2xl — `md:` (768px viewport) would put two tiles in
                        that, ~176px each, which is narrower than the figures they carry. Stacked
                        until the pane is genuinely wide. */}
                    <div className={embedded ? '' : 'grid grid-cols-1 2xl:grid-cols-2 gap-4 text-center'}>
                        <ImpactMetric
                            layout={embedded ? 'row' : 'card'}
                            title="Net Worth (Today)"
                            subtitle="everything owned, less everything owed"
                            beforeValue={baselineOnly ? null : before.netWorth}
                            afterValue={netWorthAfter}
                            breakdown={netWorthBreakdownOverride ?? undefined}
                        />
                        {/* The estate's "before" is the DO-NOTHING projection over the same period
                            when the caller can supply one (owner-caught, 2026-08-26). Comparing a
                            five-year projection with today's net worth credits the strategy with
                            growth that would have happened anyway, and can report a gain where the
                            honest comparison is a loss — on one real village scenario the tile said
                            "+$167,743 vs today" while the same plan left $388,974 LESS than staying
                            put. Modules that do not yet compute a baseline keep the old comparison,
                            which the "vs today" caption at least labels accurately. */}
                        <ImpactMetric
                            layout={embedded ? 'row' : 'card'}
                            // The client's name for it first, the technical name beside it — the same words
                            // the walk-in tiles and the strategy cards use (owner, 2026-09-22).
                            title={legacyTitle}
                            subtitle={
                                legacyBaselineValue != null && !baselineOnly
                                    ? `the legacy estimate after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}, against ${baselineLabel || 'doing nothing'}`
                                    : `the legacy estimate: what would be left after ${legacyYears} ${legacyYears === 1 ? 'year' : 'years'}`
                            }
                            beforeValue={baselineOnly ? null : (legacyBaselineValue ?? before.netWorth)}
                            afterValue={legacyEstimate}
                            isLegacy={!baselineOnly && legacyBaselineValue == null}
                            // The caption NAMES this module's own benchmark. Hardcoding "vs staying
                            // put" put a Retirement Village phrase on an Aged Care plan, where staying
                            // put is not an option and the benchmark is entering care with no plan.
                            legacyCaption={legacyBaselineValue != null && !baselineOnly ? `vs ${baselineLabel || 'doing nothing'}` : undefined}
                            breakdown={legacyBreakdownOverride ?? undefined}
                        />
                    </div>
                </div>
            </div>
            )}
            {/* Embedded, the way through to the projections is one of the panel's own two exits —
                repeating it here would be the third link to the same modal on one surface. */}
            {!embedded && (
                <div className="mt-4 flex justify-end">
                    <button onClick={onViewProjections} className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800">
                        <Eye size={16} /> View Projections
                    </button>
                </div>
            )}
        </div>
    );
};

export default FinancialImpactPanel;
